
import { useState, useEffect } from "react";
import { FileText, Users, Wrench, FileSpreadsheet } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface ActivityLog {
  id: string;
  action: string;
  project_name: string | null;
  customer_name: string | null;
  created_at: string;
}

export const useDashboardData = (userId: string | undefined) => {
  const [stats, setStats] = useState([
    { name: "Active Estimates", value: "0", icon: FileText, color: "text-blue-500", path: "/estimates" },
    { name: "Total Customers", value: "0", icon: Users, color: "text-green-500", path: "/customers" },
    { name: "Pending Invoices", value: "0", icon: FileSpreadsheet, color: "text-amber-500", path: "/invoices" },
    { name: "Recent Projects", value: "0", icon: Wrench, color: "text-purple-500", path: "/projects" },
  ]);
  const [recentActivity, setRecentActivity] = useState<ActivityLog[]>([]);
  const [isStatsLoading, setIsStatsLoading] = useState(true);

  // Fetch stats from Supabase
  useEffect(() => {
    const fetchStats = async () => {
      if (!userId) {
        return;
      }
      
      try {
        // Fetch customer count
        const { count: customerCount, error: customerError } = await supabase
          .from('customers')
          .select('*', { count: 'exact', head: true });

        if (customerError) {
          console.error('Error fetching customer count:', customerError);
          return;
        }

        // Fetch active estimates count (status = 'sent')
        const { count: activeEstimatesCount, error: estimatesError } = await supabase
          .from('estimates')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'sent');

        if (estimatesError) {
          console.error('Error fetching active estimates count:', estimatesError);
          return;
        }

        // Fetch pending invoices count (status = 'sent' or 'overdue')
        const { count: pendingInvoicesCount, error: pendingInvoicesError } = await supabase
          .from('invoices')
          .select('*', { count: 'exact', head: true })
          .in('status', ['sent', 'overdue']);

        if (pendingInvoicesError) {
          console.error('Error fetching pending invoices count:', pendingInvoicesError);
          return;
        }

        // Fetch recent projects count (created in the last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const { count: recentProjectsCount, error: projectsError } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', thirtyDaysAgo.toISOString());

        if (projectsError) {
          console.error('Error fetching recent projects count:', projectsError);
          return;
        }

        // Update the stats array with the actual counts
        setStats(currentStats => 
          currentStats.map(stat => {
            if (stat.name === "Total Customers") {
              return { ...stat, value: String(customerCount || 0) };
            }
            if (stat.name === "Active Estimates") {
              return { ...stat, value: String(activeEstimatesCount || 0) };
            }
            if (stat.name === "Pending Invoices") {
              return { ...stat, value: String(pendingInvoicesCount || 0) };
            }
            if (stat.name === "Recent Projects") {
              return { ...stat, value: String(recentProjectsCount || 0) };
            }
            return stat;
          })
        );

      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, [userId]);

  // Fetch recent activity
  useEffect(() => {
    const fetchActivity = async () => {
      if (!userId) {
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('activity_logs')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(10);

        if (error) {
          console.error('Error fetching activity:', error);
          return;
        }

        setRecentActivity(data || []);
      } catch (error) {
        console.error('Error fetching activity:', error);
      } finally {
        setIsStatsLoading(false);
      }
    };

    fetchActivity();

    // Set up real-time subscription for new activity
    const channel = supabase
      .channel('activity_logs_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activity_logs'
        },
        (payload) => {
          setRecentActivity(currentActivity => {
            const newActivity = [payload.new as ActivityLog, ...currentActivity];
            return newActivity.slice(0, 10); // Keep only the 10 most recent
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  return {
    stats,
    recentActivity,
    isStatsLoading
  };
};
